// ──────────────────────────────────────────────
// BackgroundThrottleGuard — RTCPeerConnection loopback to prevent
// Chrome background tab timer throttling during recording.
//
// Chrome aggressively clamps setInterval to ≤1/min after ~5 min hidden.
// Holding an active RTCPeerConnection with a media track exempts the tab.
// This is a local-only loopback — no STUN/TURN, no network traffic.
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

export class BackgroundThrottleGuard {
  private pc1: RTCPeerConnection | null = null;
  private pc2: RTCPeerConnection | null = null;
  private _isActive = false;

  get isActive(): boolean {
    return this._isActive;
  }

  async activate(stream: MediaStream): Promise<void> {
    // Idempotent: tear down previous pair before creating new one
    if (this.pc1 || this.pc2) {
      this.deactivate();
    }

    if (typeof RTCPeerConnection === 'undefined') {
      console.warn('[ThrottleGuard] RTCPeerConnection not available — guard skipped');
      SentryService.addBreadcrumb({
        category: 'recording.throttle-guard',
        message: 'RTCPeerConnection not available — throttle guard skipped',
        level: 'warning',
      });
      return;
    }

    try {
      const config: RTCConfiguration = { iceServers: [] };
      this.pc1 = new RTCPeerConnection(config);
      this.pc2 = new RTCPeerConnection(config);

      // Exchange ICE candidates
      this.pc1.onicecandidate = (e) => {
        if (e.candidate && this.pc2) {
          void this.pc2.addIceCandidate(e.candidate).catch(() => {});
        }
      };
      this.pc2.onicecandidate = (e) => {
        if (e.candidate && this.pc1) {
          void this.pc1.addIceCandidate(e.candidate).catch(() => {});
        }
      };

      // Add mic tracks from the recording stream to pc1
      for (const track of stream.getAudioTracks()) {
        this.pc1.addTrack(track, stream);
      }

      // SDP handshake
      const offer = await this.pc1.createOffer();
      await this.pc1.setLocalDescription(offer);
      await this.pc2.setRemoteDescription(offer);

      const answer = await this.pc2.createAnswer();
      await this.pc2.setLocalDescription(answer);
      await this.pc1.setRemoteDescription(answer);

      // Monitor connection health
      this.pc1.onconnectionstatechange = () => {
        if (!this.pc1) return;
        const state = this.pc1.connectionState;
        if (state === 'failed' || state === 'disconnected') {
          console.warn('[ThrottleGuard] Connection degraded:', state);
          SentryService.addBreadcrumb({
            category: 'recording.throttle-guard',
            message: `RTC loopback connection degraded: ${state}`,
            level: 'warning',
          });
        }
      };

      this._isActive = true;

      console.log('[ThrottleGuard] RTC loopback activated — pc1.connectionState:', this.pc1.connectionState);
      SentryService.addBreadcrumb({
        category: 'recording.throttle-guard',
        message: 'RTC loopback throttle guard activated',
        level: 'info',
      });
    } catch (err) {
      // Non-blocking: recording continues without throttle protection
      console.warn('[ThrottleGuard] Activation failed:', err);
      this.cleanup();
      SentryService.addBreadcrumb({
        category: 'recording.throttle-guard',
        message: `Throttle guard activation failed: ${err instanceof Error ? err.message : String(err)}`,
        level: 'warning',
      });
    }
  }

  deactivate(): void {
    if (!this.pc1 && !this.pc2) return;

    this.cleanup();

    console.log('[ThrottleGuard] Deactivated');
    SentryService.addBreadcrumb({
      category: 'recording.throttle-guard',
      message: 'RTC loopback throttle guard deactivated',
      level: 'info',
    });
  }

  private cleanup(): void {
    if (this.pc1) {
      this.pc1.onicecandidate = null;
      this.pc1.onconnectionstatechange = null;
      try {
        this.pc1.close();
      } catch {
        /* ignore */
      }
      this.pc1 = null;
    }
    if (this.pc2) {
      this.pc2.onicecandidate = null;
      try {
        this.pc2.close();
      } catch {
        /* ignore */
      }
      this.pc2 = null;
    }
    this._isActive = false;
  }
}
